/* eslint-disable no-plusplus */
import axios from 'axios';
import { toast } from 'react-toastify';
import store from '../states/store';

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL + process.env.REACT_APP_END_POINT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: '',
  },
  timeout: 1000 * 2400, // 1000 = 1 sec
});

// intercept all endpoint and add authorization token
api.interceptors.request.use((config) => {
  // set authorization token from store
  const { token } = store.getState();
  return {
    ...config,
    headers: {
      ...config.header,
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 403 expired token
    // 401 unauthorized
    if (error.response.status === 403 || error.response.status === 401) {
      const preventRefresh = [
        'v1/user/auth/login',
        'v1/user/auth/verify-otp',
        'v1/user/auth/generate-otp',
        'v1/user/authenticate',
      ];
      if (!preventRefresh.includes(error.config.url)) {
        window.location.href = '/unauthenticated';
      } else {
        toast.dismiss();
        toast.warning(error?.response?.data?.message);
      }
    }
  },
);

/**
 * Method on building Form Data for nested array values
 * @param {String} key - string
 * @param {Array} value - array of the key being pass
 * @param {FormData|Object} formData - Form Data Object where to append the value
 * @returns {Void}
 */
const formDataBuildArray = (key, value, formData) => {
  for (let index = 0; index < value.length; index++) {
    if (value[index] instanceof File) {
      formData.append(`${key}[]`, value[index]);
    } else if (value[index] instanceof Object) {
      Object.keys(value[index]).forEach((k) => {
        if (value[index][k] instanceof FileList) {
          if (value[index][k][0]) {
            formData.append(`${key}[${index}][${k}]`, value[index][k][0]);
          }
        } else if (value[index][k] instanceof Array) {
          formDataBuildArray(`${key}[${index}][${k}]`, value[index][k], formData);
        } else {
          formData.append(`${key}[${index}][${k}]`, value[index][k]);
        }
      });
    } else if (value[index] instanceof Array) {
      formDataBuildArray(`${key}[${index}]`, value[index], formData);
    } else {
      formData.append(`${key}[]`, value[index]);
    }
  }
};

/**
 * Method for making ajax calls to the site's api
 * @param {String} endpoint - the endpoint url
 * @param {String} method api methid POST | GET | DELETE
 * @param {Object|String} [data] - key:value pairs of the data to be sent to server
 * @param {Boolean} withFiles - has file attachments
 * @returns {Promise}
 */
async function makeApiCall(
  endpoint,
  method,
  data,
  withFiles = false,
  otherOptions = {},
  version = 'v1',
) {
  let request = {
    url: `${version}${endpoint}`,
    method,
    data,
    ...otherOptions,
  };

  if (method === 'GET') {
    request = { ...request, params: data };
  }

  if (withFiles && data) {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof FileList) {
        if (value[0]) {
          formData.append(key, value[0]);
        }
      } else if (typeof value === 'boolean') {
        formData.append(key, value ? 1 : 0);
      } else if (value instanceof Array) {
        formDataBuildArray(key, value, formData);
      } else {
        // if (value) { // removed this line the value 0 is being excluded
        formData.append(key, value);
        // }
      }
    });

    request = { ...request, data: formData };
  }

  const result = await api(request)
    .then((response) => {
      if (response) {
        return response;
      }
      const error = new Error(response.statusText);
      error.response = response;
      throw error;
    })
    .catch((error) => {
      if (axios.isCancel) return 'request_cancelled';

      return error.response;
    });

  return result;
}

export default makeApiCall;
